import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { createCashflowItem } from "@/api/cashflow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CategoryField,
  ErrorPanel,
  FieldError,
  FirmnessField,
  OrgField,
} from "@/pages/cashflow/ManualFields";
import { errorDetail, isValidAmount, normalizeAmount } from "@/pages/cashflow/utils";
import type { CashflowCategory, CashflowOrg } from "@/types/api";

const schema = z.object({
  title: z.string().trim().min(1, "Назовите платёж").max(200),
  org_inn: z.string().min(1, "Выберите организацию"),
  category: z.string().min(1, "Выберите статью"),
  firmness: z.enum(["committed", "planned", "assumption"]),
  date: z.string().min(1, "Укажите дату"),
  amount: z.string().refine(isValidAmount, "Сумма > 0, до двух знаков после запятой"),
  comment: z.string(),
});

type Values = z.infer<typeof schema>;

interface Props {
  orgInns: string[];
  orgs: CashflowOrg[];
  categories: CashflowCategory[];
  /**
   * Дата расчёта календаря (с бэка) — она же нижняя граница даты строки:
   * бэк сравнивает с московским «сегодня», а не с датой браузера.
   */
  asOf: string;
  onCreated: () => void;
  onCancel: () => void;
}

export function ManualItemForm({
  orgInns,
  orgs,
  categories,
  asOf,
  onCreated,
  onCancel,
}: Props): React.ReactElement {
  // Ответ сервера держим рядом с формой: 422 с разбором полей по тосту не
  // прочитать, а он исчезает раньше, чем успеваешь понять причину.
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      org_inn: orgInns[0] ?? "",
      category: "",
      firmness: "planned",
      date: asOf,
      amount: "",
      comment: "",
    },
  });

  const mut = useMutation({
    mutationFn: (v: Values) =>
      createCashflowItem({
        title: v.title.trim(),
        org_inn: v.org_inn,
        category: v.category,
        firmness: v.firmness,
        date: v.date,
        amount: normalizeAmount(v.amount),
        comment: v.comment.trim() === "" ? null : v.comment.trim(),
      }),
    onSuccess: () => {
      setServerError(null);
      toast.success("Строка добавлена");
      reset();
      onCreated();
    },
    onError: (err: unknown) => setServerError(errorDetail(err)),
  });

  return (
    <form
      className="space-y-4 rounded-md border p-4"
      onSubmit={handleSubmit((v) => {
        setServerError(null);
        mut.mutate(v);
      })}
    >
      <div className="grid gap-3 md:grid-cols-3">
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="item_title">Название</Label>
          <Input id="item_title" placeholder="напр. Оплата поставщику" {...register("title")} />
          <FieldError message={errors.title?.message} />
        </div>
        <OrgField
          id="item_org"
          field={register("org_inn")}
          error={errors.org_inn?.message}
          orgInns={orgInns}
          orgs={orgs}
        />
        <CategoryField
          id="item_category"
          field={register("category")}
          error={errors.category?.message}
          categories={categories}
        />
        <FirmnessField
          id="item_firmness"
          field={register("firmness")}
          error={errors.firmness?.message}
        />
        <div className="space-y-1.5">
          <Label htmlFor="item_date">Дата</Label>
          {/* Прошлые платежи уже сидят в остатке календаря — бэк такую дату
              не принимает (422), поэтому и выбрать её нельзя. Граница —
              дата расчёта с бэка (московское «сегодня»), а не дата браузера:
              часовой пояс машины бухгалтера тут ни при чём. */}
          <Input id="item_date" type="date" min={asOf} {...register("date")} />
          <FieldError message={errors.date?.message} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="item_amount">Сумма, ₽</Label>
          <Input id="item_amount" placeholder="100000.00" {...register("amount")} />
          <FieldError message={errors.amount?.message} />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="item_comment">Комментарий</Label>
          <Input id="item_comment" {...register("comment")} />
        </div>
      </div>

      {serverError !== null && (
        <ErrorPanel detail={`Не удалось добавить строку: ${serverError}`} />
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={mut.isPending}>
          {mut.isPending ? "Сохранение…" : "Добавить строку"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </form>
  );
}
